import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Upload, Camera, X, Plus, Trash2, Wallet, CreditCard, Landmark } from "lucide-react";
import { compressImage } from "../../utils/compressImage";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const input = "w-full px-3 py-2.5 rounded-xl border text-sm bg-white";

export const emptyLine = () => ({ key: `${Date.now()}-${Math.random()}`, description: "", amount: "", category: "", invoice_number: "", invoice_date: "", file: null });
export const emptyPayout = () => ({ method: "iban", iban: "", wallet_id: "", paypal_id: "", walletCheck: null, useSaved: true });
const usingSaved = (p, saved, m) => p.useSaved && !!(m === "iban" ? saved?.iban_masked : m === "paypal" ? saved?.paypal_masked : null);

export function validateExpense(payout, lines, saved) {
  if (usingSaved(payout, saved, payout.method)) payout = { ...payout, iban: "saved", paypal_id: "saved" };
  if (payout.method === "iban" && !payout.iban) return "IBAN is required for IBAN payouts";
  if (payout.method === "wallet" && !payout.wallet_id) return "Wallet ID is required for wallet payouts";
  if (payout.method === "wallet" && payout.walletCheck?.valid === false) return "This wallet ID isn't valid. Please double-check the ID.";
  if (payout.method === "paypal" && !payout.paypal_id) return "PayPal ID / email is required for PayPal payouts";
  const bad = lines.findIndex(l => !l.description || !(parseFloat(l.amount) > 0));
  return bad >= 0 ? `Line ${bad + 1}: description and positive amount required` : null;
}

export function appendExpense(fd, payout, lines, saved) {
  fd.append("payout_method", payout.method);
  if (usingSaved(payout, saved, payout.method)) {
    fd.append("use_saved", "1");
    payout = { ...payout, iban: "", paypal_id: "" };
  }
  if (payout.method === "iban") fd.append("payout_iban", payout.iban);
  if (payout.method === "wallet") fd.append("payout_wallet_id", payout.wallet_id);
  if (payout.method === "paypal") fd.append("payout_paypal_id", payout.paypal_id);
  fd.append("lines", JSON.stringify(lines.map((l, i) => ({
    description: l.description, amount: parseFloat(l.amount) || 0, category: l.category,
    invoice_number: l.invoice_number, invoice_date: l.invoice_date, file_key: l.file ? `file_${i}` : null,
  }))));
  lines.forEach((l, i) => { if (l.file) fd.append(`file_${i}`, l.file); });
}

function SavedChoice({ masked, sub, payout, set, testid }) {
  return (
    <div className="space-y-1.5 mb-2" data-testid={`payout-saved-choice-${testid}`}>
      {[[true, `Use saved: ${masked}${sub ? ` (${sub})` : ""}`], [false, "Enter different details"]].map(([v, label]) => (
        <label key={String(v)} className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
          <input type="radio" checked={payout.useSaved === v} onChange={() => set({ useSaved: v })} className="accent-[#1e3a5f]" data-testid={`payout-saved-${testid}-${v ? "use" : "other"}`} />
          <span className={v ? "font-mono text-xs" : ""}>{label}</span>
        </label>
      ))}
    </div>
  );
}

export function PayoutPicker({ payout, setPayout, ownWalletId, saved }) {
  const set = (patch) => setPayout({ ...payout, ...patch });
  const pick = (method) => set(method === "wallet" && ownWalletId && !payout.wallet_id
    ? { method, wallet_id: ownWalletId, walletCheck: { valid: true, display_name: "your own wallet", kind: "member" } }
    : { method });
  const check = async () => {
    const wid = (payout.wallet_id || "").trim().toUpperCase();
    if (!wid) return set({ walletCheck: null });
    try { const r = await axios.get(`${API}/wallet-id/validate/${encodeURIComponent(wid)}`); set({ walletCheck: r.data }); }
    catch { set({ walletCheck: { valid: false } }); }
  };
  const wc = payout.walletCheck;
  return (
    <div>
      <label className="text-xs text-slate-500 mb-1 block">Payout Method *</label>
      <div className="grid grid-cols-3 gap-2 mb-2">
        {[["iban", "IBAN", Landmark], ["wallet", "Wallet", Wallet], ["paypal", "PayPal", CreditCard]].map(([id, label, Ico]) => (
          <button key={id} type="button" onClick={() => pick(id)} data-testid={`payout-${id}`}
            className={`flex flex-col items-center gap-1 py-2 rounded-xl border text-xs font-semibold ${payout.method === id ? "bg-[#1e3a5f] text-white border-[#1e3a5f]" : "bg-white text-slate-500 border-slate-200"}`}>
            <Ico className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>
      {payout.method === "iban" && saved?.iban_masked && <SavedChoice masked={saved.iban_masked} sub={saved.iban_holder} payout={payout} set={set} testid="iban" />}
      {payout.method === "paypal" && saved?.paypal_masked && <SavedChoice masked={saved.paypal_masked} payout={payout} set={set} testid="paypal" />}
      {payout.method === "iban" && !usingSaved(payout, saved, "iban") && <input value={payout.iban} onChange={e => set({ iban: e.target.value.toUpperCase() })} placeholder="DE89 3704 0044 0532 0130 00" className={`${input} font-mono`} data-testid="payout-iban-input" required />}
      {payout.method === "wallet" && (
        <>
          <input value={payout.wallet_id} onChange={e => set({ wallet_id: e.target.value.toUpperCase(), walletCheck: null })} onBlur={check}
            placeholder="MM-26-XXXXXX or MG-26-XXXXXX" data-testid="payout-wallet-id-input" required
            className={`${input} font-mono ${wc?.valid === false ? "border-red-300 bg-red-50" : wc?.valid ? "border-green-300 bg-green-50" : ""}`} />
          {wc?.valid && <p className="text-[10px] text-green-700 mt-1" data-testid="wallet-check-ok">✓ Wallet found: {wc.display_name} ({wc.kind})</p>}
          {ownWalletId && <p className="text-[10px] text-slate-500 mt-1" data-testid="wallet-own-hint">{payout.wallet_id === ownWalletId ? "Paid into your own wallet — replace the ID to pay someone else's wallet." : <button type="button" onClick={() => set({ wallet_id: ownWalletId, walletCheck: { valid: true, display_name: "your own wallet", kind: "member" } })} className="underline" data-testid="wallet-use-own-btn">Use my own wallet ({ownWalletId})</button>}</p>}
          {wc?.valid === false && <p className="text-[10px] text-red-600 mt-1" data-testid="wallet-check-err">Wallet ID not recognised or account is inactive. Double-check the ID.</p>}
        </>
      )}
      {payout.method === "paypal" && !usingSaved(payout, saved, "paypal") && <input value={payout.paypal_id} onChange={e => set({ paypal_id: e.target.value })} placeholder="paypal@example.com or PayPal.Me/handle" className={input} data-testid="payout-paypal-input" required />}
    </div>
  );
}

function ReceiptPicker({ file, onFile }) {
  return (
    <div>
      <label className="text-xs text-slate-500 mb-1 block">Invoice / Receipt</label>
      <div className="flex gap-2">
        <label className="flex-1 flex items-center gap-2 px-3 py-2.5 rounded-xl border cursor-pointer hover:bg-slate-50 text-sm text-slate-600 overflow-hidden bg-white">
          <Upload className="w-4 h-4 flex-shrink-0" /><span className="truncate">{file ? file.name : "Choose file..."}</span>
          <input type="file" accept="image/*,.pdf" onChange={e => onFile(e.target.files[0])} className="hidden" data-testid="line-file-input" />
        </label>
        <label className="flex items-center gap-1 px-3 py-2.5 rounded-xl bg-[#1e3a5f] text-white text-sm cursor-pointer hover:opacity-90" title="Take a photo">
          <Camera className="w-4 h-4" /> Photo
          <input type="file" accept="image/*" capture="environment" onChange={e => onFile(e.target.files[0])} className="hidden" />
        </label>
        {file && <button type="button" onClick={() => onFile(null)} className="px-2.5 py-2.5 rounded-xl border border-slate-200 text-slate-500" title="Clear"><X className="w-4 h-4" /></button>}
      </div>
    </div>
  );
}

function LineCard({ ln, i, categories, canRemove, update, remove }) {
  const onFile = async (raw) => {
    if (!raw) return update({ file: null });
    const big = raw.type?.startsWith("image/") && raw.type !== "image/gif" && raw.size >= 500 * 1024;
    try {
      const out = big ? await compressImage(raw) : raw;
      update({ file: out });
      if (big && out.size < raw.size) toast.success(`Photo compressed to ${(out.size / 1024).toFixed(0)} KB`);
    } catch { update({ file: raw }); }
  };
  return (
    <div className="rounded-xl border border-slate-200 p-3 space-y-3 bg-slate-50/50" data-testid={`expense-line-${i}`}>
      <div className="flex justify-between items-center">
        <p className="text-xs font-bold text-[#1e3a5f]">Line {i + 1}</p>
        {canRemove && <button type="button" onClick={remove} className="text-xs text-red-500 flex items-center gap-1" data-testid={`remove-line-${i}`}><Trash2 className="w-3.5 h-3.5" /> Remove</button>}
      </div>
      <div>
        <label className="text-xs text-slate-500 mb-1 block">Description *</label>
        <textarea value={ln.description} onChange={e => update({ description: e.target.value })} rows={2} className={`${input} resize-none`} required data-testid={`line-desc-${i}`} />
      </div>
      {categories.length > 0 && (
        <select value={ln.category} onChange={e => update({ category: e.target.value })} className={input} data-testid={`line-category-${i}`}>
          <option value="">Select category (optional)</option>
          {categories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
        </select>
      )}
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-xs text-slate-500 mb-1 block">Amount (EUR) *</label>
          <input type="number" step="0.01" min="0" value={ln.amount} onChange={e => update({ amount: e.target.value })} className={input} required data-testid={`line-amount-${i}`} />
        </div>
        <div>
          <label className="text-xs text-slate-500 mb-1 block">Invoice Date</label>
          <input type="date" value={ln.invoice_date} onChange={e => update({ invoice_date: e.target.value })} className={input} data-testid={`line-invdate-${i}`} />
        </div>
      </div>
      <input value={ln.invoice_number} onChange={e => update({ invoice_number: e.target.value })} placeholder="Invoice number" className={input} data-testid={`line-invnum-${i}`} />
      <ReceiptPicker file={ln.file} onFile={onFile} />
    </div>
  );
}

export function ExpenseLines({ lines, setLines, categories }) {
  const total = lines.reduce((s, l) => s + (parseFloat(l.amount) || 0), 0);
  return (
    <div className="space-y-3">
      {lines.map((ln, i) => (
        <LineCard key={ln.key} ln={ln} i={i} categories={categories} canRemove={lines.length > 1}
          update={(p) => setLines(ls => ls.map((l, j) => (j === i ? { ...l, ...p } : l)))}
          remove={() => setLines(ls => ls.filter((_, j) => j !== i))} />
      ))}
      <button type="button" onClick={() => setLines(ls => [...ls, emptyLine()])} data-testid="add-line-btn"
        className="w-full py-2.5 rounded-xl border-2 border-dashed border-[#1e3a5f]/40 text-[#1e3a5f] font-semibold text-sm flex items-center justify-center gap-2">
        <Plus className="w-4 h-4" /> Add another line
      </button>
      <div className="flex justify-between items-center px-2">
        <p className="text-sm text-slate-500">{lines.length} line{lines.length === 1 ? "" : "s"}</p>
        <p className="text-lg font-bold text-[#1e3a5f]" data-testid="expense-total">Total: &euro;{total.toFixed(2)}</p>
      </div>
    </div>
  );
}
